import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import {
  BookingApiService,
  SiteUserMessageSubmitRequest
} from '../../../../core/services/booking-api.service';
import { RecaptchaService } from '../../../../core/services/recaptcha.service';

@Component({
  selector: 'app-message-us-section',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './message-us-section.component.html',
  styleUrl: './message-us-section.component.css'
})
export class MessageUsSectionComponent {
  private static readonly successMessage = 'Thank you, your message was sent and we will respond to you shortly.';
  private static readonly failureMessage = 'Sorry, there was an error trying to send your message. Please wait and try again.';

  private readonly fb = inject(FormBuilder);
  private readonly bookingApi = inject(BookingApiService);
  private readonly recaptchaService = inject(RecaptchaService);

  protected submitting = false;
  protected submittedMessage = '';
  protected submitError = '';
  protected readonly contactMessageForm = this.fb.nonNullable.group({
    name: ['', [Validators.required, this.requiredTrimmedValidator]],
    email: ['', [Validators.required, this.emailFormatValidator]],
    message: ['', [Validators.required, this.minimumMessageLengthValidator]],
    honeypotWebsite: [''],
    honeypotReference: ['']
  });

  protected async submitMessage(): Promise<void> {
    this.submittedMessage = '';
    this.submitError = '';

    if (this.contactMessageForm.invalid) {
      this.contactMessageForm.markAllAsTouched();
      return;
    }

    try {
      this.submitting = true;

      const recaptchaToken = await this.recaptchaService.executeSiteUserMessageRequest();
      const request = this.buildRequest(recaptchaToken);
      const response = await firstValueFrom(this.bookingApi.submitSiteUserMessage(request));

      if (!response.success) {
        this.submitError = MessageUsSectionComponent.failureMessage;
        return;
      }

      this.contactMessageForm.reset({
        name: '',
        email: '',
        message: '',
        honeypotWebsite: '',
        honeypotReference: ''
      });

      this.submittedMessage = MessageUsSectionComponent.successMessage;
    } catch (error: unknown) {
      this.submitError = this.getSubmitErrorMessage(error);
    } finally {
      this.submitting = false;
    }
  }

  private buildRequest(recaptchaToken: string): SiteUserMessageSubmitRequest {
    const controls = this.contactMessageForm.controls;

    return {
      name: controls.name.value.trim(),
      email: controls.email.value.trim(),
      messageBody: controls.message.value.trim(),
      companyName: controls.honeypotWebsite.value.trim(),
      subject: controls.honeypotReference.value.trim(),
      sourcePage: '/contact-us',
      recaptchaToken
    };
  }

  private getSubmitErrorMessage(error?: unknown): string {
    if (error instanceof HttpErrorResponse) {
      if (typeof error.error === 'string' && error.error.trim().length > 0) {
        return error.error;
      }

      if (
        error.error &&
        typeof error.error === 'object' &&
        'message' in error.error &&
        typeof error.error.message === 'string' &&
        error.error.message.trim().length > 0
      ) {
        return error.error.message;
      }
    }

    return MessageUsSectionComponent.failureMessage;
  }

  private requiredTrimmedValidator(control: { value: string }) {
    return control.value.trim().length > 0 ? null : { required: true };
  }

  private emailFormatValidator(control: AbstractControl<string>): ValidationErrors | null {
    const email = control.value.trim();
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email) ? null : { email: true };
  }

  private minimumMessageLengthValidator(control: { value: string }) {
    return control.value.trim().length > 10 ? null : { minlength: true };
  }
}
